import { format, isAfter, isBefore, isValid, parse } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Field } from '../../Field/index.js';
import { Input } from '../../Input/index.js';
import { MAX_DATE, MIN_DATE } from '../utilities/constants.js';
import { getDataTestId } from '../../../utils/utils.js';
import { useDateFormat } from '../hooks/useDateFormat.js';
import { useMinMaxDate } from '../hooks/useMinMaxDate.js';
import { getTodayDate, validateSelectedDate } from '../utilities/date.logic.js';
const defaultDateParser = (pattern) => (inputString, context) => {
    const parsed = parse(inputString, pattern, context);
    if (isValid(parsed)) {
        return parsed;
    }
    return 'Unable to parse date';
};
/**
 * An input for dates.
 *
 * Attempts to parse the date when the input blurs or the user presses enter. If
 * successful, it will invoke `onSelectDate` with the parsed date. Otherwise, it
 * displays an error message.
 *
 * The default parser supports ISO formats and a variety of localized formats.
 */
export function DateInput({ dataMetrics, dataTestId: propsDataTestId, dateParser, formatPattern = 'PPPPp', className, onSelectDate, max: propsMax = MAX_DATE, min: propsMin = MIN_DATE, selectedDate, placeholder = '', inputProps, ...fieldProps }) {
    const { pattern } = useDateFormat(formatPattern);
    const { min, max } = useMinMaxDate({ min: propsMin, max: propsMax, pattern });
    const [inputDate, setInputDate] = useState();
    const [inputString, setInputString] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const intl = useIntl();
    const dataTestId = getDataTestId('date_input', propsDataTestId);
    useEffect(() => {
        if (!selectedDate) {
            setInputString('');
            setInputDate(undefined);
            setErrorMessage('');
        }
        else if (validateSelectedDate({ selectedDate, min, max }) &&
            (inputDate ? format(inputDate, pattern) !== format(selectedDate, pattern) : true)) {
            setInputString(format(selectedDate, pattern));
            setInputDate(selectedDate);
            setErrorMessage('');
        }
    }, [selectedDate, pattern, min, max]);
    function handleDateChange(e) {
        setInputString(e.currentTarget.value);
    }
    const context = inputDate ?? getTodayDate();
    const appliedDateParser = (dateParser && useCallback(dateParser, [])) ?? defaultDateParser(pattern);
    function handleInputString(value) {
        if (!value) {
            setErrorMessage('');
            return;
        }
        const result = appliedDateParser(inputString, context);
        if (typeof result === 'string') {
            setErrorMessage(result);
        }
        else if (inputDate ? format(inputDate, pattern) === format(result, pattern) : false) {
            setErrorMessage('');
        }
        else if (isAfter(result, max)) {
            setErrorMessage(intl.formatMessage({
                defaultMessage: '{date, date, ::MMddyyyy} {date, time, short} is after the maximum, {max, date, ::MMddyyyy} {max, time, short}',
                id: 'iKATsM',
                description: 'Date input is after the maximum allowed'
            }, { date: result, max: max }));
        }
        else if (isBefore(result, min)) {
            setErrorMessage(intl.formatMessage({
                defaultMessage: '{date, date, ::MMddyyyy} {date, time, short} is before the minimum, {min, date, ::MMddyyyy} {min, time, short}',
                id: 'ZpfFQW',
                description: 'Date input is before the minimum allowed'
            }, { date: result, min }));
        }
        else {
            onSelectDate(result);
            setInputDate(result);
            setErrorMessage('');
        }
    }
    function handleBlur(e) {
        handleInputString(e.currentTarget.value);
    }
    function handleKeyDown(e) {
        if (e.key === 'Enter') {
            handleInputString(e.currentTarget.value);
        }
    }
    return (React.createElement(Field, { dataTestId: getDataTestId('field', dataTestId), dataMetrics: dataMetrics, ...fieldProps, control: React.createElement(Input, { addClassName: className?.inputClassName, appearance: errorMessage ? 'error' : undefined, dataTestId: getDataTestId('input', dataTestId), onBlur: handleBlur, onChange: handleDateChange, onKeyDown: handleKeyDown, placeholder: placeholder, value: inputString, ...inputProps }), error: errorMessage }));
}
//# sourceMappingURL=date_input.js.map